import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-trivia", title: "Why Trivia Quizzes?" },
  { id: "general-knowledge", title: "General Knowledge Tests" },
  { id: "geography-trivia", title: "Geography & Flags" },
  { id: "uncommon-facts", title: "Uncommon Knowledge" },
];

const quizResources = [
  { name: "Britannica Trivia Collection", url: "https://www.britannica.com/quiz/browse", description: "Hundreds of trivia topics" },
  { name: "General Knowledge Quiz", url: "https://play.howstuffworks.com/quiz/99-people-cant-crush-this-general-knowledge-quiz-can-you", description: "General knowledge challenge" },
  { name: "Know-It-All Quiz", url: "https://www.proprofs.com/quiz-school/story.php?title=3dq-test-your-basic-general-knowledge", description: "Basic knowledge test" },
  { name: "Not-So-Common Knowledge", url: "https://www.britannica.com/quiz/not-so-common-knowledge", description: "Obscure trivia facts" },
  { name: "Explore India Quiz", url: "https://www.britannica.com/quiz/explore-india", description: "Regional trivia deep dive" },
];

const relatedTopics = [
  { name: "Science Quizzes", path: "/quizzes/science-quizzes/", description: "Scientific knowledge" },
  { name: "History Quizzes", path: "/quizzes/history-quizzes/", description: "Historical events" },
  { name: "Fun Quizzes", path: "/quizzes/fun-quizzes-to-take/", description: "Entertainment quizzes" },
];

const TriviaQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Trivia Quizzes: Test Your General Knowledge"
      metaTitle="Trivia Quizzes | General Knowledge Tests & Trivia Games 2025"
      metaDescription="Challenge your general knowledge with trivia quizzes covering every topic. Geography, flags, random facts - prove you're the ultimate trivia champion."
      keywords="trivia quizzes, general knowledge quiz, trivia games, geography quiz, flags quiz, random trivia, knowledge test"
      canonicalUrl="/quizzes/trivia-quizzes/"
      category="Trivia"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Trivia quizzes are the ultimate test of your general knowledge. From random facts to obscure details you never knew you needed to know—trivia challenges keep your brain sharp and satisfy your curiosity."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-trivia" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Trivia Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          There's a reason trivia nights are so popular—testing your knowledge is addictively fun. Trivia quizzes challenge you across every topic imaginable.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Brain exercise:</strong> Keep your mind sharp with diverse challenges</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn random facts:</strong> Pick up fascinating information</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Competitive fun:</strong> See how you compare to others</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Party entertainment:</strong> Great for game nights</span>
          </li>
        </ul>
      </section>

      <section id="general-knowledge" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          General Knowledge <span className="text-gradient">Tests</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://play.howstuffworks.com/quiz/99-people-cant-crush-this-general-knowledge-quiz-can-you" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">"99% of People Can't Crush This General Knowledge Quiz"</a> from HowStuffWorks challenges you across multiple topics:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <a href="https://www.pewresearch.org/science/quiz/science-knowledge-quiz/" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Science & Nature</h4>
            <p className="text-sm text-muted-foreground">Basic scientific facts</p>
          </a>
          <a href="https://www.britannica.com/quiz/history-buff-quiz" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">History & Events</h4>
            <p className="text-sm text-muted-foreground">Important dates and figures</p>
          </a>
          <a href="https://www.britannica.com/quiz/browse" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Geography</h4>
            <p className="text-sm text-muted-foreground">Countries, capitals, landmarks</p>
          </a>
          <a href="https://www.britannica.com/quiz/pop-culture-quiz" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Culture & Society</h4>
            <p className="text-sm text-muted-foreground">Art, literature, traditions</p>
          </a>
        </div>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          ProProfs' <a href="https://www.proprofs.com/quiz-school/story.php?title=3dq-test-your-basic-general-knowledge" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">General Knowledge Quiz for Know-It-Alls</a> covers basics that everyone should know—from which vitamin strengthens bones to basic math and vocabulary.
        </p>
      </section>

      <section id="geography-trivia" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Geography & <span className="text-gradient">Flags</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Geography quizzes are incredibly popular on <a href="https://www.britannica.com/quiz/browse" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Britannica</a>. "Name That World Flag!" and "Match the Capital Cities" are perennial favorites:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Flag identification:</strong> Can you recognize all 195 country flags?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Capital cities:</strong> Match over 190 capitals to their countries</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Continent knowledge:</strong> What belongs where?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Famous landmarks:</strong> Identify world wonders</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://www.britannica.com/quiz/explore-india" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Explore India Quiz</a> and similar regional quizzes deep-dive into specific countries, testing detailed knowledge of their geography, government, and culture.
        </p>
      </section>

      <section id="uncommon-facts" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Uncommon <span className="text-gradient">Knowledge</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/not-so-common-knowledge" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Not-So-Common Knowledge</a> quiz goes beyond textbook facts to test truly obscure information:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Which legendary band was originally called "Warsaw"?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Which city hosted the 1900 Olympics?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Who wrote the song made famous by Johnny Cash at San Quentin?</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          These quizzes reward deep knowledge and curiosity. Even if you don't know the answers, you'll learn fascinating facts that make great conversation starters.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default TriviaQuizzes;
